import { useState } from 'react';

interface YouTubeInputProps {
  onFetchTranscript: (url: string) => void;
  isLoading: boolean;
}

export function YouTubeInput({ onFetchTranscript, isLoading }: YouTubeInputProps) {
  const [url, setUrl] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      onFetchTranscript(url.trim());
    }
  };

  const isValidYouTubeUrl = (input: string): boolean => {
    return /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)/.test(input);
  };

  const showWarning = url.trim() && !isValidYouTubeUrl(url);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <label htmlFor="youtube-input" className="block text-sm font-medium text-surface-300 mb-3">
        Enter YouTube URL
      </label>

      <div className="flex gap-3">
        <div className={`flex-1 relative rounded-xl transition-all duration-300 ${
          isFocused ? 'ring-2 ring-primary-500/30' : ''
        } ${showWarning ? 'ring-2 ring-yellow-500/30' : ''}`}>
          {/* YouTube icon */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-500">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </div>

          <input
            id="youtube-input"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="https://www.youtube.com/watch?v=..."
            className={`input-field pl-12 pr-4 ${
              showWarning ? 'border-yellow-500/50 focus:border-yellow-500' : ''
            }`}
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !url.trim() || !!showWarning}
          className="btn-primary flex items-center gap-2.5 whitespace-nowrap"
        >
          {isLoading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Fetching...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>Transform</span>
            </>
          )}
        </button>
      </div>

      {/* Warning message */}
      {showWarning && (
        <div className="mt-3 flex items-center gap-2 text-yellow-400 animate-fade-in">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-sm">Please enter a valid YouTube URL</span>
        </div>
      )}

      {/* Help text */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-surface-500">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Supported: youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/...</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-surface-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Only works for videos with captions/subtitles enabled</span>
        </div>
      </div>
    </form>
  );
}
